import React from 'react';
import { Clock, Coffee, Utensils, Users, Briefcase, LogOut, AlertCircle } from 'lucide-react';
import type { SimulationEventData, SimulationEventType } from '../../types/simulation.types';

interface EventTimelineProps {
  events: SimulationEventData[];
  className?: string;
}

const EVENT_ICONS: Record<SimulationEventType, React.ReactNode> = {
  AGENT_ENTRY: <LogOut className="w-4 h-4 rotate-180" />,
  AGENTS_ARRIVE: <LogOut className="w-4 h-4 rotate-180" />,
  WORK_BEGIN: <Briefcase className="w-4 h-4" />,
  TASK_STARTED: <Briefcase className="w-4 h-4" />,
  COFFEE_BREAK: <Coffee className="w-4 h-4" />,
  LUNCH_BREAK: <Utensils className="w-4 h-4" />,
  TASK_COMPLETED: <Briefcase className="w-4 h-4" />,
  COORDINATION_MEETING: <Users className="w-4 h-4" />,
  PROGRESS_UPDATE: <Clock className="w-4 h-4" />,
  WORK_COMPLETE: <Briefcase className="w-4 h-4" />,
  AGENTS_LEAVE: <LogOut className="w-4 h-4" />,
  ERROR_OCCURRED: <AlertCircle className="w-4 h-4" />,
};

const EVENT_COLORS: Record<SimulationEventType, string> = {
  AGENT_ENTRY: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  AGENTS_ARRIVE: 'bg-blue-100 text-blue-700 border-blue-200',
  WORK_BEGIN: 'bg-green-100 text-green-700 border-green-200',
  TASK_STARTED: 'bg-purple-100 text-purple-700 border-purple-200',
  COFFEE_BREAK: 'bg-amber-100 text-amber-700 border-amber-200',
  LUNCH_BREAK: 'bg-orange-100 text-orange-700 border-orange-200',
  TASK_COMPLETED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  COORDINATION_MEETING: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  PROGRESS_UPDATE: 'bg-slate-100 text-slate-700 border-slate-200',
  WORK_COMPLETE: 'bg-green-100 text-green-700 border-green-200',
  AGENTS_LEAVE: 'bg-slate-100 text-slate-700 border-slate-200',
  ERROR_OCCURRED: 'bg-red-100 text-red-700 border-red-200',
};

export const EventTimeline: React.FC<EventTimelineProps> = ({ events, className }) => {
  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // Show only recent events (last 10)
  const recentEvents = sortedEvents.slice(0, 10);

  if (recentEvents.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Event Timeline</h3>
        <div className="text-center py-8">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No events yet</p>
          <p className="text-slate-400 text-sm mt-1">Events will appear here as the simulation runs</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-slate-900 mb-4">Event Timeline</h3>

      <div className="space-y-3">
        {recentEvents.map((event, index) => {
          const colorClass = EVENT_COLORS[event.eventType] || EVENT_COLORS.PROGRESS_UPDATE;
          const icon = EVENT_ICONS[event.eventType] || EVENT_ICONS.PROGRESS_UPDATE;

          return (
            <div
              key={event.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${colorClass} transition-all ${
                index === 0 ? 'ring-2 ring-offset-1 ring-blue-400' : ''
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">{icon}</div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{event.description}</p>
                <p className="text-xs opacity-75 mt-1">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </p>
              </div>

              {event.agentId && (
                <div className="flex-shrink-0 text-xs font-medium opacity-75">
                  Agent
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sortedEvents.length > 10 && (
        <p className="text-xs text-slate-500 text-center mt-4">
          Showing {recentEvents.length} of {sortedEvents.length} events
        </p>
      )}
    </div>
  );
};
